import { useEffect, useState } from 'react';

import { crmFicheHref } from '../../../../supabase/functions/_shared/crmFiche';
import { useSidebar } from '../../../contexts/SidebarContext';
import { useOpportunityGestures } from '../../../hooks/useOpportunityGestures';
import { useOpportunitySheet, type SheetOpportunity } from '../../../hooks/useOpportunitySheet';
import { newMissionHref } from '../../../lib/bureau/newMission';
import { exchangesOf } from '../../../lib/crm/accountSheet';
import { REF } from '../../bureau/encre';
import { EntityMissionsBlock } from '../EntityMissionsBlock';
import { ContactsBlock, ExchangesBlock } from './AccountSheetBlocks';
import { EncreSheet, ReReadError, SheetEmpty } from './EncreSheet';
import { OutcomeButtons, OutcomePanel, type Closing } from './OpportunityOutcome';
import { DealBlock, LeadBlock } from './OpportunitySheetBlocks';
import { OpportunitySheetEdit } from './OpportunitySheetEdit';
import { OpportunitySheetHeader } from './OpportunitySheetHeader';

/**
 * A deal's working sheet, in Encre (PLAN-ENCRE-OUTILS O3, mock-up « Encre-Fiche »): where it stands
 * and moving it (stage, won, lost, reopened), what Otto does about it and asking him, what it brings,
 * the people involved, the latest exchanges, its onboarding lead. « Modifier » edits its figures
 * here. Opened from every name of the app and from the pipeline (`openOpportunitySidebar`).
 */
export function OpportunitySheet({ opportunityId, onClose }: { opportunityId: string | null; onClose: () => void }) {
  const sheet = useOpportunitySheet(opportunityId);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => setEditing(false), [opportunityId]);
  const deal = sheet.opportunity;

  return (
    <EncreSheet
      open={opportunityId !== null}
      label={deal ? `Fiche de ${deal.name}` : "Fiche de l'opportunité"}
      focusKey={`${opportunityId}:${editing}`}
      dirty={editing && dirty}
      onClose={onClose}
    >
      {!deal ? (
        sheet.error ? (
          <p className="text-sm text-red-600 dark:text-red-400">Lecture impossible : {sheet.error}</p>
        ) : sheet.missing ? (
          <SheetEmpty>Cette opportunité est introuvable : elle a été supprimée, ou n'existe pas.</SheetEmpty>
        ) : (
          <div className="space-y-4 pt-2" aria-busy="true">
            <div className="h-14 w-2/3 animate-pulse rounded-2xl bg-desk-subtle" />
            <div className="h-24 animate-pulse rounded-2xl bg-desk-subtle" />
          </div>
        )
      ) : (
        <>
          {sheet.error && <ReReadError error={sheet.error} />}
          <OpportunitySheetBody deal={deal} sheet={sheet} editing={editing} setEditing={setEditing} onDirty={setDirty} onClose={onClose} />
        </>
      )}
    </EncreSheet>
  );
}

function OpportunitySheetBody({
  deal,
  sheet,
  editing,
  setEditing,
  onDirty,
  onClose,
}: {
  deal: SheetOpportunity;
  sheet: ReturnType<typeof useOpportunitySheet>;
  editing: boolean;
  setEditing: (next: boolean) => void;
  onDirty: (dirty: boolean) => void;
  onClose: () => void;
}) {
  const { openAccountSidebar, openContactSidebar, openAppointmentSidebar, openMeetingReportSidebar, openLeadSidebar } = useSidebar();
  const gestures = useOpportunityGestures(deal.id, sheet.reload, openLeadSidebar);
  const [closing, setClosing] = useState<Closing>(null);
  useEffect(() => setClosing(null), [deal.id]);
  const rows = { loading: !sheet.rowsReady && !sheet.rowsError, error: sheet.rowsError };
  // The people named on the deal first (`dealContacts`), then its account's: the first five.
  const contacts = sheet.rows.contacts.slice(0, 5);

  return (
    <>
      <OpportunitySheetHeader
        deal={deal}
        outcome={<OutcomeButtons deal={deal} busy={gestures.busy} onClose={setClosing} onReopen={() => void gestures.reopen()} />}
        newMissionHref={newMissionHref({ opportunity: deal.id, client: deal.account_id })}
        ficheHref={crmFicheHref('opportunity', deal.id)}
        editing={editing}
        onOpenAccount={openAccountSidebar}
        onEdit={() => {
          setClosing(null);
          setEditing(true);
        }}
        onNavigate={onClose}
      />
      {gestures.error && (
        <p role="alert" className="mb-6 rounded-[10px] bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {gestures.error}
        </p>
      )}
      {editing ? (
        <OpportunitySheetEdit deal={deal} onSaved={sheet.reload} onDone={() => setEditing(false)} onDirty={onDirty} />
      ) : (
        <>
          {closing && deal.status === 'open' && (
            <OutcomePanel
              deal={deal}
              stages={sheet.stages}
              closing={closing}
              busy={gestures.busy}
              onApply={(patch, withLead) => void gestures.apply(patch, withLead).then((ok) => ok && setClosing(null))}
              onCancel={() => setClosing(null)}
            />
          )}
          <EntityMissionsBlock kind="opportunity" entity={{ id: deal.id, name: deal.name }} account={deal.accountRef} />
          <DealBlock deal={deal} stages={sheet.stages} busy={gestures.busy} onStage={(stage) => void gestures.moveTo(stage)} />
          <ContactsBlock
            contacts={contacts}
            total={sheet.rows.contacts.length}
            state={rows}
            onOpen={openContactSidebar}
            more={
              <button type="button" onClick={() => openAccountSidebar(deal.account_id)} className={`text-sm font-medium ${REF}`}>
                Tout voir
              </button>
            }
          />
          <ExchangesBlock
            exchanges={exchangesOf(sheet.rows.appointments, sheet.rows.notes, { reports: sheet.rows.reports })}
            state={rows}
            onOpenAppointment={openAppointmentSidebar}
            onOpenReport={openMeetingReportSidebar}
            noteHref={(id) => `/admin/notes?note=${encodeURIComponent(id)}`}
            onNavigate={onClose}
          />
          <LeadBlock deal={deal} busy={gestures.busy} onOpenLead={openLeadSidebar} onCreateLead={() => void gestures.openLead()} />
        </>
      )}
    </>
  );
}
